import { useState, useEffect } from 'react';
import axios from 'axios';
import { Eye, CheckCircle, Clock, DollarSign, Truck, Package, Printer, MapPin, Loader2, Trash2, Copy, ExternalLink, Download, FileText } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Function to generate and download receipt
const downloadAdminReceipt = (order) => {
  const statusLabels = {
    pending: 'En attente',
    confirmed: 'Confirmée',
    processing: 'En préparation',
    shipped: 'Expédiée',
    delivered: 'Livrée',
    cancelled: 'Annulée'
  };
  
  const paymentLabels = {
    paid: 'Payé',
    pending: 'En attente',
    cod_pending: 'À la livraison'
  };

  const receiptHTML = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Reçu Admin ${order.order_id}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; background: #f5f5f5; padding: 20px; }
    .receipt { max-width: 700px; margin: 0 auto; background: white; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.1); }
    .header { background: linear-gradient(135deg, #1a1a1a 0%, #333 100%); color: #d4af37; padding: 30px; text-align: center; }
    .header h1 { font-size: 28px; letter-spacing: 4px; margin-bottom: 5px; }
    .header p { color: #fff; font-size: 12px; letter-spacing: 2px; }
    .header .admin-badge { background: #d4af37; color: #1a1a1a; padding: 5px 15px; border-radius: 20px; font-size: 11px; font-weight: bold; display: inline-block; margin-top: 10px; }
    .gold-bar { height: 4px; background: linear-gradient(90deg, #d4af37, #f4e4bc, #d4af37); }
    .content { padding: 30px; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 25px; }
    .info-box { background: #f9f9f9; padding: 15px; border-radius: 8px; border-left: 4px solid #d4af37; }
    .info-box h3 { color: #1a1a1a; font-size: 12px; margin-bottom: 10px; letter-spacing: 1px; }
    .info-box p { color: #666; font-size: 13px; margin: 5px 0; }
    .info-box strong { color: #1a1a1a; }
    .items-title { color: #d4af37; font-size: 14px; font-weight: bold; letter-spacing: 2px; margin-bottom: 15px; border-bottom: 2px solid #d4af37; padding-bottom: 8px; }
    .item { display: flex; justify-content: space-between; padding: 12px 0; border-bottom: 1px solid #eee; }
    .item-name { color: #333; flex: 1; }
    .item-qty { color: #888; width: 80px; text-align: center; }
    .item-price { color: #d4af37; font-weight: bold; width: 100px; text-align: right; }
    .totals { margin-top: 20px; padding: 20px; background: #1a1a1a; border-radius: 8px; }
    .total-row { display: flex; justify-content: space-between; color: #fff; padding: 8px 0; }
    .total-row.grand { border-top: 2px solid #d4af37; margin-top: 10px; padding-top: 15px; font-size: 18px; }
    .total-row.grand span:last-child { color: #d4af37; }
    .footer { background: #1a1a1a; color: #888; padding: 20px; text-align: center; font-size: 12px; }
    .footer .gold { color: #d4af37; }
    @media print { body { padding: 0; } .receipt { box-shadow: none; } }
  </style>
</head>
<body>
  <div class="receipt">
    <div class="header">
      <h1>ALINA ELEGANCE</h1>
      <p>REÇU DE COMMANDE</p>
      <span class="admin-badge">COPIE ADMIN</span>
    </div>
    <div class="gold-bar"></div>
    <div class="content">
      <div class="info-grid">
        <div class="info-box">
          <h3>📋 COMMANDE</h3>
          <p><strong>N°:</strong> #${order.order_id.slice(-8).toUpperCase()}</p>
          <p><strong>Date:</strong> ${new Date(order.created_at).toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
          <p><strong>Statut:</strong> ${statusLabels[order.order_status] || order.order_status}</p>
          <p><strong>Paiement:</strong> ${paymentLabels[order.payment_status] || order.payment_status}</p>
        </div>
        <div class="info-box">
          <h3>👤 CLIENT</h3>
          <p><strong>Nom:</strong> ${order.customer_name || order.shipping_address?.name || 'N/A'}</p>
          <p><strong>Email:</strong> ${order.user_email || 'N/A'}</p>
          ${order.shipping_address?.phone ? `<p><strong>Tél:</strong> ${order.shipping_address.phone}</p>` : ''}
        </div>
      </div>
      
      ${order.shipping_address ? `
      <div class="info-box" style="margin-bottom: 20px;">
        <h3>📍 ADRESSE DE LIVRAISON</h3>
        <p>${order.shipping_address.name || ''}</p>
        <p>${order.shipping_address.street || order.shipping_address.address || ''}</p>
        <p>${order.shipping_address.city || ''}, ${order.shipping_address.province || ''} ${order.shipping_address.postal_code || ''}</p>
      </div>
      ` : ''}
      
      <div class="items-title">ARTICLES COMMANDÉS</div>
      ${order.items.map(item => `
        <div class="item">
          <span class="item-name">${item.name}</span>
          <span class="item-qty">× ${item.quantity}</span>
          <span class="item-price">$${(item.price * item.quantity).toFixed(2)}</span>
        </div>
      `).join('')}
      
      <div class="totals">
        <div class="total-row">
          <span>Sous-total</span>
          <span>$${order.subtotal?.toFixed(2) || '0.00'}</span>
        </div>
        <div class="total-row">
          <span>Livraison</span>
          <span>$${order.delivery_fee?.toFixed(2) || '0.00'}</span>
        </div>
        <div class="total-row grand">
          <span>TOTAL</span>
          <span>$${order.total.toFixed(2)} CAD</span>
        </div>
      </div>
      
      ${order.tracking_pin ? `
      <div class="info-box" style="margin-top: 20px; border-left-color: #06b6d4;">
        <h3>📦 SUIVI</h3>
        <p><strong>N° de suivi:</strong> ${order.tracking_pin}</p>
        ${order.canada_post_service_name ? `<p><strong>Service:</strong> ${order.canada_post_service_name}</p>` : ''}
      </div>
      ` : ''}
      
      ${order.notes ? `
      <div class="info-box" style="margin-top: 20px; border-left-color: #f59e0b;">
        <h3>📝 NOTES</h3>
        <p>${order.notes}</p>
      </div>
      ` : ''}
    </div>
    <div class="footer">
      <p class="gold">ALINA ELEGANCE - Document Administratif</p>
      <p>Imprimé le ${new Date().toLocaleDateString('fr-CA')} à ${new Date().toLocaleTimeString('fr-CA')}</p>
    </div>
  </div>
  <script>window.onload = function() { window.print(); }</script>
</body>
</html>`;

  const blob = new Blob([receiptHTML], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
};

const statusColors = {
  pending: 'bg-orange-100 text-orange-700 border-orange-300',
  confirmed: 'bg-blue-100 text-blue-700 border-blue-300',
  processing: 'bg-purple-100 text-purple-700 border-purple-300',
  shipped: 'bg-cyan-100 text-cyan-700 border-cyan-300',
  delivered: 'bg-green-100 text-green-700 border-green-300',
  cancelled: 'bg-red-100 text-red-700 border-red-300'
};

const statusLabels = {
  pending: 'En attente',
  confirmed: 'Confirmée',
  processing: 'En préparation',
  shipped: 'Expédiée',
  delivered: 'Livrée',
  cancelled: 'Annulée'
};

const deliveryMethodLabels = {
  delivery: { label: 'Livraison locale', icon: Truck, color: 'bg-blue-100 text-blue-700' },
  pickup: { label: 'Ramassage', icon: MapPin, color: 'bg-green-100 text-green-700' },
  canada_post: { label: 'Poste Canada', icon: Package, color: 'bg-red-100 text-red-700' }
};

const paymentLabels = {
  paid: { label: 'Payé', color: 'bg-green-100 text-green-700 border-green-300' },
  pending: { label: 'En attente', color: 'bg-orange-100 text-orange-700 border-orange-300' },
  cod_pending: { label: 'À la livraison', color: 'bg-orange-100 text-orange-700 border-orange-300' },
  interac: { label: 'Interac en attente', color: 'bg-orange-100 text-orange-700 border-orange-300' }
};

// Calculate recommended box dimensions from order items
const calculateBoxDimensions = (items) => {
  if (!items || items.length === 0) {
    return { length: 20, width: 15, height: 10, weight: 0.5 };
  }
  
  const PACKAGING_MARGIN = 2; // 2cm margin
  const PACKAGING_WEIGHT = 0.1; // 100g for packaging
  
  let maxLength = 0;
  let maxWidth = 0;
  let totalHeight = 0;
  let totalWeight = PACKAGING_WEIGHT;
  
  items.forEach(item => {
    const length = item.length || 10;
    const width = item.width || 10;
    const height = item.height || 5;
    const weight = item.weight || 0.1;
    const qty = item.quantity || 1;
    
    maxLength = Math.max(maxLength, length);
    maxWidth = Math.max(maxWidth, width);
    totalHeight += height * qty;
    totalWeight += weight * qty;
  });
  
  return {
    length: Math.ceil(Math.max(maxLength + PACKAGING_MARGIN, 10)),
    width: Math.ceil(Math.max(maxWidth + PACKAGING_MARGIN, 10)),
    height: Math.ceil(Math.max(totalHeight + PACKAGING_MARGIN, 5)),
    weight: Math.round(totalWeight * 100) / 100
  };
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [generatingLabel, setGeneratingLabel] = useState(false);
  const [shippingInfoModal, setShippingInfoModal] = useState(false);
  const [shippingInfo, setShippingInfo] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(res.data || []);
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/orders/${orderId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Statut mis à jour');
      fetchOrders();
      if (selectedOrder?.order_id === orderId) {
        setSelectedOrder(prev => prev ? { ...prev, order_status: status } : null);
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const markAsPaid = async (orderId, isPaid) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/orders/${orderId}/payment?paid=${isPaid}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(isPaid ? 'Commande marquée comme payée' : 'Paiement annulé');
      fetchOrders();
      if (selectedOrder?.order_id === orderId) {
        setSelectedOrder(prev => prev ? { 
          ...prev, 
          payment_status: isPaid ? 'paid' : 'pending',
          order_status: isPaid && prev.order_status === 'pending' ? 'confirmed' : prev.order_status
        } : null);
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du paiement');
    }
  };

  const generateShippingLabel = (order) => {
    if (!order.shipping_address) {
      toast.error('Adresse de livraison manquante');
      return;
    }
    
    // Calculate box dimensions and weight
    const box = calculateBoxDimensions(order.items);
    
    // Extract province from postal code
    const postalCode = order.shipping_address.postal_code?.replace(/\s/g, '').toUpperCase() || '';
    const provinceMap = {
      'A': 'NL', 'B': 'NS', 'C': 'PE', 'E': 'NB', 'G': 'QC', 'H': 'QC', 'J': 'QC',
      'K': 'ON', 'L': 'ON', 'M': 'ON', 'N': 'ON', 'P': 'ON',
      'R': 'MB', 'S': 'SK', 'T': 'AB', 'V': 'BC',
      'X': 'NT', 'Y': 'YT'
    };
    const provinceNames = {
      'NL': 'Terre-Neuve-et-Labrador', 'NS': 'Nouvelle-Écosse', 'PE': 'Île-du-Prince-Édouard',
      'NB': 'Nouveau-Brunswick', 'QC': 'Québec', 'ON': 'Ontario', 'MB': 'Manitoba',
      'SK': 'Saskatchewan', 'AB': 'Alberta', 'BC': 'Colombie-Britannique',
      'NT': 'Territoires du Nord-Ouest', 'YT': 'Yukon'
    };
    const provinceCode = order.shipping_address.province || provinceMap[postalCode[0]] || 'ON';
    
    // Prepare shipping info for the modal
    const info = {
      orderId: order.order_id,
      recipient: {
        name: order.shipping_address.name || '',
        address: order.shipping_address.street || order.shipping_address.address || '',
        city: order.shipping_address.city || '',
        province: provinceCode,
        provinceName: provinceNames[provinceCode] || provinceCode,
        postalCode: order.shipping_address.postal_code || '',
        phone: order.shipping_address.phone || ''
      },
      sender: {
        name: 'ALINA ELEGANCE',
        address: '130 Topham Terrace',
        city: 'Ottawa',
        province: 'ON',
        provinceName: 'Ontario',
        postalCode: 'K4A 0E9',
        phone: '613-123-4567'
      },
      package: {
        weight: box.weight,
        length: box.length,
        width: box.width,
        height: box.height
      },
      service: order.canada_post_service_name || 'Regular Parcel'
    };
    
    setShippingInfo(info);
    setShippingInfoModal(true);
    
    // Open Canada Post shipping page
    window.open('https://www.canadapost-postescanada.ca/shippingtools-outilsexpedition/fr/shipment?tableau=vrai', '_blank');
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié!`);
  };

  const downloadLabel = async (orderId) => {
    try {
      const token = localStorage.getItem('token');
      window.open(`${API_URL}/api/shipping/label/${orderId}?token=${token}`, '_blank');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette commande ? Cette action est irréversible.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Commande supprimée');
      setDialogOpen(false);
      setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const getPaymentInfo = (status) => {
    return paymentLabels[status] || paymentLabels.pending;
  };
  
  const getDeliveryInfo = (method) => {
    return deliveryMethodLabels[method] || deliveryMethodLabels.delivery;
  };

  const openOrderDialog = (order) => {
    setSelectedOrder(order);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setTimeout(() => setSelectedOrder(null), 200);
  };

  // Safe getter for nested properties
  const getShippingName = (order) => {
    return order?.shipping_address?.name || order?.user_name || 'Client';
  };

  const getShippingStreet = (order) => {
    return order?.shipping_address?.street || order?.shipping_address?.address || '-';
  };

  // Calculate totals
  const paidOrders = orders.filter(o => o.payment_status === 'paid');
  const pendingOrders = orders.filter(o => o.payment_status !== 'paid');
  const totalPaid = paidOrders.reduce((sum, o) => sum + (o.total || 0), 0);
  const totalPending = pendingOrders.reduce((sum, o) => sum + (o.total || 0), 0);

  return (
    <AdminLayout>
      <div data-testid="admin-orders">
        <div className="mb-8">
          <h1 className="font-heading text-3xl text-brand-black">Commandes</h1>
          <p className="text-brand-dark/60">{orders.length} commandes au total</p>
        </div>

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-green-600 text-sm font-medium">Payées</p>
                <p className="text-green-700 text-xl font-bold">${totalPaid.toFixed(2)} CAD</p>
                <p className="text-green-500 text-xs">{paidOrders.length} commandes</p>
              </div>
            </div>
          </div>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-yellow-600 text-sm font-medium">En attente</p>
                <p className="text-yellow-700 text-xl font-bold">${totalPending.toFixed(2)} CAD</p>
                <p className="text-yellow-500 text-xs">{pendingOrders.length} commandes</p>
              </div>
            </div>
          </div>
          
          <div className="bg-brand-gold/10 border border-brand-gold/20 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-brand-gold/20 rounded-lg flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-brand-gold" />
              </div>
              <div>
                <p className="text-brand-gold text-sm font-medium">Total Ventes</p>
                <p className="text-brand-black text-xl font-bold">${(totalPaid + totalPending).toFixed(2)} CAD</p>
                <p className="text-brand-dark/60 text-xs">{orders.length} commandes</p>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12 text-brand-dark/60">Aucune commande</div>
        ) : (
          <div className="bg-brand-white border border-brand-gold/20 rounded-lg overflow-x-auto shadow-sm">
            <Table>
              <TableHeader>
                <TableRow className="border-brand-gold/20 bg-brand-cream">
                  <TableHead className="text-brand-dark/80 font-semibold">Commande</TableHead>
                  <TableHead className="text-brand-dark/80 font-semibold">Date</TableHead>
                  <TableHead className="text-brand-dark/80 font-semibold">Client</TableHead>
                  <TableHead className="text-brand-dark/80 font-semibold">Total</TableHead>
                  <TableHead className="text-brand-dark/80 font-semibold">Livraison</TableHead>
                  <TableHead className="text-brand-dark/80 font-semibold">Paiement</TableHead>
                  <TableHead className="text-brand-dark/80 font-semibold">Statut</TableHead>
                  <TableHead className="text-brand-dark/80 font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map(order => {
                  const deliveryInfo = getDeliveryInfo(order.delivery_method);
                  const DeliveryIcon = deliveryInfo.icon;
                  return (
                  <TableRow key={order.order_id} className="border-brand-gold/10 hover:bg-brand-cream">
                    <TableCell className="text-brand-black font-medium">
                      #{(order.order_id || '').slice(-8).toUpperCase()}
                    </TableCell>
                    <TableCell className="text-brand-dark/80">
                      {order.created_at ? new Date(order.created_at).toLocaleDateString('fr-FR') : '-'}
                    </TableCell>
                    <TableCell className="text-brand-dark/80">
                      {getShippingName(order)}
                    </TableCell>
                    <TableCell className="text-brand-gold font-semibold">
                      ${(order.total || 0).toFixed(2)} CAD
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge className={`${deliveryInfo.color} border text-xs flex items-center gap-1 w-fit`}>
                          <DeliveryIcon className="h-3 w-3" />
                          {deliveryInfo.label}
                        </Badge>
                        {order.delivery_method === 'canada_post' && order.canada_post_service_name && (
                          <span className="text-xs text-red-600 font-medium">
                            {order.canada_post_service_name}
                          </span>
                        )}
                        {order.tracking_pin && (
                          <span className="text-xs text-brand-gold font-mono">
                            📦 {order.tracking_pin}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge className={`${getPaymentInfo(order.payment_status).color} border`}>
                          {getPaymentInfo(order.payment_status).label}
                        </Badge>
                        {order.payment_status !== 'paid' && (
                          <Button 
                            size="sm" 
                            onClick={() => markAsPaid(order.order_id, true)}
                            className="bg-green-500 hover:bg-green-600 text-white text-xs h-7 px-2"
                          >
                            <CheckCircle className="h-3 w-3 mr-1" /> Payé
                          </Button>
                        )}
                        {order.payment_status === 'paid' && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => markAsPaid(order.order_id, false)}
                            className="text-brand-dark/50 hover:text-red-500 text-xs h-7 px-2"
                          >
                            Annuler
                          </Button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select value={order.order_status || 'pending'} onValueChange={(v) => updateStatus(order.order_id, v)}>
                        <SelectTrigger className="w-36 bg-brand-white border-brand-gold/20 text-brand-black h-8">
                          <Badge className={`${statusColors[order.order_status || 'pending']} border`}>
                            {statusLabels[order.order_status || 'pending']}
                          </Badge>
                        </SelectTrigger>
                        <SelectContent className="bg-brand-white border-brand-gold/20">
                          {Object.entries(statusLabels).map(([key, label]) => (
                            <SelectItem key={key} value={key} className="text-brand-dark">{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openOrderDialog(order)} className="text-brand-dark/60 hover:text-brand-gold" title="Voir détails">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => downloadAdminReceipt(order)} 
                          className="text-brand-dark/60 hover:text-brand-gold"
                          title="Imprimer reçu"
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => deleteOrder(order.order_id)} 
                          className="text-brand-dark/60 hover:text-red-500"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Order Details Dialog */}
        <Dialog open={dialogOpen} onOpenChange={closeDialog}>
          <DialogContent className="bg-brand-white border-brand-gold/20 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-brand-black">
                Commande #{selectedOrder ? (selectedOrder.order_id || '').slice(-8).toUpperCase() : ''}
              </DialogTitle>
            </DialogHeader>
            
            {selectedOrder && (
              <div className="space-y-6">
                {/* Payment Status Banner */}
                <div className={`p-4 rounded-lg ${selectedOrder.payment_status === 'paid' ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      {selectedOrder.payment_status === 'paid' ? (
                        <CheckCircle className="h-6 w-6 text-green-600" />
                      ) : (
                        <Clock className="h-6 w-6 text-yellow-600" />
                      )}
                      <div>
                        <p className={`font-semibold ${selectedOrder.payment_status === 'paid' ? 'text-green-700' : 'text-yellow-700'}`}>
                          {selectedOrder.payment_status === 'paid' ? 'Paiement reçu' : 'En attente de paiement'}
                        </p>
                        <p className="text-sm text-brand-dark/80">
                          Méthode: {selectedOrder.payment_method === 'interac' ? 'Virement Interac' : selectedOrder.payment_method === 'cod' ? 'À la livraison' : selectedOrder.payment_method === 'card' ? 'Carte bancaire' : selectedOrder.payment_method || 'Non spécifié'}
                        </p>
                      </div>
                    </div>
                    {selectedOrder.payment_status !== 'paid' && (
                      <Button 
                        onClick={() => markAsPaid(selectedOrder.order_id, true)}
                        className="bg-green-500 hover:bg-green-600 text-white"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" /> Marquer payé
                      </Button>
                    )}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-brand-dark/60 text-sm">Date</p>
                    <p className="text-brand-black">{selectedOrder.created_at ? new Date(selectedOrder.created_at).toLocaleString('fr-FR') : '-'}</p>
                  </div>
                  <div>
                    <p className="text-brand-dark/60 text-sm">Statut commande</p>
                    <Badge className={`${statusColors[selectedOrder.order_status || 'pending']} border mt-1`}>
                      {statusLabels[selectedOrder.order_status || 'pending']}
                    </Badge>
                  </div>
                </div>

                <div>
                  <h4 className="text-brand-black font-semibold mb-3">Articles</h4>
                  <div className="space-y-2">
                    {(selectedOrder.items || []).map((item, i) => (
                      <div key={i} className="flex justify-between bg-brand-cream p-3 rounded">
                        <div>
                          <p className="text-brand-black">{item.name || 'Produit'}</p>
                          <p className="text-brand-dark/60 text-sm">Qté: {item.quantity || 1} × ${(item.price || 0).toFixed(2)}</p>
                        </div>
                        <p className="text-brand-gold font-semibold">${((item.price || 0) * (item.quantity || 1)).toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recommended Box Size */}
                {selectedOrder.delivery_method === 'canada_post' && (
                  <div>
                    <h4 className="text-brand-black font-semibold mb-3">📦 Boîte recommandée</h4>
                    {(() => {
                      const box = calculateBoxDimensions(selectedOrder.items);
                      return (
                        <div className="bg-gradient-to-r from-brand-gold/10 to-brand-gold/5 border border-brand-gold/30 p-4 rounded-lg">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <div className="flex justify-between">
                                <span className="text-brand-dark/70">Longueur:</span>
                                <span className="font-bold text-brand-black">{box.length} cm</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-brand-dark/70">Largeur:</span>
                                <span className="font-bold text-brand-black">{box.width} cm</span>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <div className="flex justify-between">
                                <span className="text-brand-dark/70">Hauteur:</span>
                                <span className="font-bold text-brand-black">{box.height} cm</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-brand-dark/70">Poids total:</span>
                                <span className="font-bold text-brand-gold">{box.weight} kg</span>
                              </div>
                            </div>
                          </div>
                          <div className="mt-3 pt-3 border-t border-brand-gold/20 text-center">
                            <p className="text-sm text-brand-dark/60">
                              Dimensions minimales: <span className="font-semibold text-brand-black">{box.length} × {box.width} × {box.height} cm</span>
                            </p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {selectedOrder.shipping_address && (
                  <div>
                    <h4 className="text-brand-black font-semibold mb-3">Livraison</h4>
                    <div className="bg-brand-cream p-3 rounded text-brand-dark/80 text-sm">
                      <p className="font-medium text-brand-black">{selectedOrder.shipping_address.name || 'Client'}</p>
                      <p>{selectedOrder.shipping_address.street || selectedOrder.shipping_address.address || '-'}</p>
                      <p>{selectedOrder.shipping_address.postal_code || ''} {selectedOrder.shipping_address.city || ''}</p>
                      {selectedOrder.shipping_address.phone && <p>{selectedOrder.shipping_address.phone}</p>}
                    </div>
                  </div>
                )}

                {/* Delivery Method Section */}
                <div>
                  <h4 className="text-brand-black font-semibold mb-3">Mode de livraison</h4>
                  <div className={`p-4 rounded-lg border ${
                    selectedOrder.delivery_method === 'canada_post' ? 'bg-red-50 border-red-200' :
                    selectedOrder.delivery_method === 'pickup' ? 'bg-green-50 border-green-200' :
                    'bg-blue-50 border-blue-200'
                  }`}>
                    <div className="flex items-center gap-3">
                      {selectedOrder.delivery_method === 'canada_post' ? (
                        <Package className="h-6 w-6 text-red-600" />
                      ) : selectedOrder.delivery_method === 'pickup' ? (
                        <MapPin className="h-6 w-6 text-green-600" />
                      ) : (
                        <Truck className="h-6 w-6 text-blue-600" />
                      )}
                      <div className="flex-1">
                        <p className={`font-semibold ${
                          selectedOrder.delivery_method === 'canada_post' ? 'text-red-700' :
                          selectedOrder.delivery_method === 'pickup' ? 'text-green-700' :
                          'text-blue-700'
                        }`}>
                          {getDeliveryInfo(selectedOrder.delivery_method).label}
                        </p>
                        {selectedOrder.delivery_method === 'canada_post' && selectedOrder.canada_post_service_name && (
                          <p className="text-sm text-red-600">
                            Service: {selectedOrder.canada_post_service_name}
                          </p>
                        )}
                        {selectedOrder.tracking_pin && (
                          <p className="text-sm font-mono text-brand-gold mt-1">
                            📦 Suivi: {selectedOrder.tracking_pin}
                          </p>
                        )}
                        <p className="text-sm text-brand-dark/60 mt-1">
                          Frais: ${(selectedOrder.delivery_fee || 0).toFixed(2)} CAD
                        </p>
                      </div>
                    </div>
                    
                    {/* Canada Post Actions */}
                    {selectedOrder.delivery_method === 'canada_post' && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {!selectedOrder.tracking_pin ? (
                          <Button 
                            onClick={() => generateShippingLabel(selectedOrder)}
                            disabled={generatingLabel}
                            className="bg-red-600 hover:bg-red-700 text-white"
                          >
                            {generatingLabel ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Génération...
                              </>
                            ) : (
                              <>
                                <Printer className="h-4 w-4 mr-2" />
                                Générer l'étiquette
                              </>
                            )}
                          </Button>
                        ) : (
                          <Button 
                            onClick={() => downloadLabel(selectedOrder.order_id)}
                            className="bg-red-600 hover:bg-red-700 text-white"
                          >
                            <Printer className="h-4 w-4 mr-2" />
                            Télécharger l'étiquette
                          </Button>
                        )}
                        {selectedOrder.tracking_pin && (
                          <Button 
                            variant="outline"
                            onClick={() => window.open(`https://www.canadapost-postescanada.ca/track-reperage/en#/details/${selectedOrder.tracking_pin}`, '_blank')}
                            className="border-red-300 text-red-700 hover:bg-red-50"
                          >
                            Suivre le colis
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-between text-xl border-t border-brand-gold/20 pt-4">
                  <span className="text-brand-black font-semibold">Total</span>
                  <span className="text-brand-gold font-bold">${(selectedOrder.total || 0).toFixed(2)} CAD</span>
                </div>
                
                {/* Delete Button */}
                <div className="border-t border-brand-gold/20 pt-4 mt-4">
                  <Button 
                    variant="outline"
                    onClick={() => deleteOrder(selectedOrder.order_id)}
                    className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Supprimer cette commande
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Shipping Info Modal for Canada Post */}
        <Dialog open={shippingInfoModal} onOpenChange={setShippingInfoModal}>
          <DialogContent className="bg-brand-white border-brand-gold/20 max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-brand-black font-heading text-xl flex items-center gap-2">
                📋 Informations d'expédition - Canada Post
              </DialogTitle>
            </DialogHeader>
            
            {shippingInfo && (
              <div className="space-y-6 mt-4">
                <p className="text-brand-dark/70 text-sm bg-blue-50 p-3 rounded-lg border border-blue-200">
                  💡 La page Canada Post s'est ouverte dans un nouvel onglet. Copiez les informations ci-dessous pour remplir le formulaire.
                </p>

                {/* Destinataire */}
                <div className="bg-brand-cream p-4 rounded-lg">
                  <h4 className="text-brand-black font-semibold mb-3 flex items-center gap-2">
                    📍 Destinataire
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="ml-auto text-xs"
                      onClick={() => copyToClipboard(
                        `${shippingInfo.recipient.name}\n${shippingInfo.recipient.address}\n${shippingInfo.recipient.city}, ${shippingInfo.recipient.province} ${shippingInfo.recipient.postalCode}`,
                        'Adresse destinataire'
                      )}
                    >
                      Copier tout
                    </Button>
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex justify-between items-center bg-white p-2 rounded cursor-pointer hover:bg-brand-gold/10" onClick={() => copyToClipboard(shippingInfo.recipient.name, 'Nom')}>
                      <span className="text-brand-dark/70">Nom:</span>
                      <span className="font-medium text-brand-black">{shippingInfo.recipient.name}</span>
                    </div>
                    <div className="flex justify-between items-center bg-white p-2 rounded cursor-pointer hover:bg-brand-gold/10" onClick={() => copyToClipboard(shippingInfo.recipient.phone, 'Téléphone')}>
                      <span className="text-brand-dark/70">Tél:</span>
                      <span className="font-medium text-brand-black">{shippingInfo.recipient.phone}</span>
                    </div>
                    <div className="col-span-2 flex justify-between items-center bg-white p-2 rounded cursor-pointer hover:bg-brand-gold/10" onClick={() => copyToClipboard(shippingInfo.recipient.address, 'Adresse')}>
                      <span className="text-brand-dark/70">Adresse:</span>
                      <span className="font-medium text-brand-black">{shippingInfo.recipient.address}</span>
                    </div>
                    <div className="flex justify-between items-center bg-white p-2 rounded cursor-pointer hover:bg-brand-gold/10" onClick={() => copyToClipboard(shippingInfo.recipient.city, 'Ville')}>
                      <span className="text-brand-dark/70">Ville:</span>
                      <span className="font-medium text-brand-black">{shippingInfo.recipient.city}</span>
                    </div>
                    <div className="flex justify-between items-center bg-white p-2 rounded cursor-pointer hover:bg-brand-gold/10" onClick={() => copyToClipboard(shippingInfo.recipient.province, 'Province')}>
                      <span className="text-brand-dark/70">Province:</span>
                      <span className="font-medium text-brand-black">{shippingInfo.recipient.provinceName} ({shippingInfo.recipient.province})</span>
                    </div>
                    <div className="col-span-2 flex justify-between items-center bg-white p-2 rounded cursor-pointer hover:bg-brand-gold/10" onClick={() => copyToClipboard(shippingInfo.recipient.postalCode, 'Code postal')}>
                      <span className="text-brand-dark/70">Code postal:</span>
                      <span className="font-bold text-brand-gold text-lg">{shippingInfo.recipient.postalCode}</span>
                    </div>
                  </div>
                </div>

                {/* Expéditeur */}
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h4 className="text-green-800 font-semibold mb-3 flex items-center gap-2">
                    🏢 Expéditeur (Votre adresse)
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="ml-auto text-xs"
                      onClick={() => copyToClipboard(
                        `${shippingInfo.sender.name}\n${shippingInfo.sender.address}\n${shippingInfo.sender.city}, ${shippingInfo.sender.province} ${shippingInfo.sender.postalCode}`,
                        'Adresse expéditeur'
                      )}
                    >
                      Copier tout
                    </Button>
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex justify-between items-center bg-white p-2 rounded cursor-pointer hover:bg-green-100" onClick={() => copyToClipboard(shippingInfo.sender.name, 'Nom entreprise')}>
                      <span className="text-brand-dark/70">Entreprise:</span>
                      <span className="font-medium text-brand-black">{shippingInfo.sender.name}</span>
                    </div>
                    <div className="flex justify-between items-center bg-white p-2 rounded cursor-pointer hover:bg-green-100" onClick={() => copyToClipboard(shippingInfo.sender.phone, 'Téléphone')}>
                      <span className="text-brand-dark/70">Tél:</span>
                      <span className="font-medium text-brand-black">{shippingInfo.sender.phone}</span>
                    </div>
                    <div className="col-span-2 flex justify-between items-center bg-white p-2 rounded cursor-pointer hover:bg-green-100" onClick={() => copyToClipboard(shippingInfo.sender.address, 'Adresse')}>
                      <span className="text-brand-dark/70">Adresse:</span>
                      <span className="font-medium text-brand-black">{shippingInfo.sender.address}</span>
                    </div>
                    <div className="flex justify-between items-center bg-white p-2 rounded cursor-pointer hover:bg-green-100" onClick={() => copyToClipboard(shippingInfo.sender.city, 'Ville')}>
                      <span className="text-brand-dark/70">Ville:</span>
                      <span className="font-medium text-brand-black">{shippingInfo.sender.city}</span>
                    </div>
                    <div className="flex justify-between items-center bg-white p-2 rounded cursor-pointer hover:bg-green-100" onClick={() => copyToClipboard(shippingInfo.sender.postalCode, 'Code postal')}>
                      <span className="text-brand-dark/70">Code postal:</span>
                      <span className="font-bold text-green-700">{shippingInfo.sender.postalCode}</span>
                    </div>
                  </div>
                </div>

                {/* Dimensions du colis */}
                <div className="bg-brand-gold/10 p-4 rounded-lg border border-brand-gold/30">
                  <h4 className="text-brand-black font-semibold mb-3 flex items-center gap-2">
                    📦 Dimensions du colis
                  </h4>
                  <div className="grid grid-cols-4 gap-3 text-sm">
                    <div className="text-center bg-white p-3 rounded cursor-pointer hover:bg-brand-gold/20" onClick={() => copyToClipboard(shippingInfo.package.weight.toString(), 'Poids')}>
                      <p className="text-brand-dark/70 text-xs">Poids</p>
                      <p className="font-bold text-brand-gold text-xl">{shippingInfo.package.weight} kg</p>
                    </div>
                    <div className="text-center bg-white p-3 rounded cursor-pointer hover:bg-brand-gold/20" onClick={() => copyToClipboard(shippingInfo.package.length.toString(), 'Longueur')}>
                      <p className="text-brand-dark/70 text-xs">Longueur</p>
                      <p className="font-bold text-brand-black text-xl">{shippingInfo.package.length} cm</p>
                    </div>
                    <div className="text-center bg-white p-3 rounded cursor-pointer hover:bg-brand-gold/20" onClick={() => copyToClipboard(shippingInfo.package.width.toString(), 'Largeur')}>
                      <p className="text-brand-dark/70 text-xs">Largeur</p>
                      <p className="font-bold text-brand-black text-xl">{shippingInfo.package.width} cm</p>
                    </div>
                    <div className="text-center bg-white p-3 rounded cursor-pointer hover:bg-brand-gold/20" onClick={() => copyToClipboard(shippingInfo.package.height.toString(), 'Hauteur')}>
                      <p className="text-brand-dark/70 text-xs">Hauteur</p>
                      <p className="font-bold text-brand-black text-xl">{shippingInfo.package.height} cm</p>
                    </div>
                  </div>
                </div>

                {/* Service */}
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <h4 className="text-red-800 font-semibold mb-2">🚚 Service recommandé</h4>
                  <p className="text-red-700 font-medium">{shippingInfo.service}</p>
                </div>

                <p className="text-brand-dark/50 text-xs text-center">
                  💡 Cliquez sur n'importe quel champ pour le copier automatiquement
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
